import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Trophy, Lock } from 'lucide-react';
import { STATUS_COLORS } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import './CompareModal.css';

const fmt = (d) => { try { return format(new Date(d), 'MMM dd, yyyy'); } catch { return '—'; } };
const fmtSalary = (job) => {
  if (!job.salaryMin && !job.salaryMax) return null;
  const cur = job.salaryCurrency || 'PHP';
  const f = n => n?.toLocaleString();
  if (job.salaryMin && job.salaryMax) {
    if (job.salaryMin === job.salaryMax) return `${cur} ${f(job.salaryMin)}`;
    return `${cur} ${f(job.salaryMin)} – ${f(job.salaryMax)}`;
  }
  if (job.salaryMin) return `${cur} ${f(job.salaryMin)}+`;
  return `Up to ${cur} ${f(job.salaryMax)}`;
};

const ROWS = [
  { key: 'role',            label: 'Role',        render: j => j.role },
  { key: 'status',          label: 'Status',      render: j => j.status, isStatus: true },
  { key: 'stage',           label: 'Stage',       render: j => j.stage || '—' },
  { key: 'source',          label: 'Source',      render: j => j.source || '—' },
  { key: 'workSetup',       label: 'Work Setup',  render: j => j.workSetup },
  { key: 'location',        label: 'Location',    render: j => j.location || '—' },
  { key: 'salary',          label: 'Salary',      render: j => fmtSalary(j) || '—' },
  { key: 'applicationDate', label: 'Applied',     render: j => fmt(j.applicationDate) },
  { key: 'nextStep',        label: 'Next Step',   render: j => j.nextStep || '—' },
  { key: 'benefits',        label: 'Benefits',    render: j => j.benefits || '—' },
  { key: 'notes',           label: 'Notes',       render: j => j.notes || '—' },
];

// Mini score bar for a single dimension
const DimBar = ({ scoreA, scoreB, winner }) => {
  const total = scoreA + scoreB || 1;
  const pctA = (scoreA / total) * 100;
  const aWins = scoreA > scoreB;
  const bWins = scoreB > scoreA;
  return (
    <div className="dim-bar-wrap">
      <span className={`dim-score ${aWins ? 'dim-score-winner' : ''}`}>{scoreA}</span>
      <div className="dim-bar-track">
        <div className="dim-bar-fill dim-bar-a" style={{ width: `${pctA}%`, opacity: aWins ? 1 : 0.45 }} />
        <div className="dim-bar-fill dim-bar-b" style={{ width: `${100 - pctA}%`, opacity: bWins ? 1 : 0.45 }} />
      </div>
      <span className={`dim-score ${bWins ? 'dim-score-winner' : ''}`}>{scoreB}</span>
    </div>
  );
};

// AI comparison result panel
const AIComparisonResult = ({ result, jobA, jobB, onExportPDF }) => {
  const { comparison } = result;
  const isA = comparison.winner === 'A';
  const isB = comparison.winner === 'B';
  const isTie = comparison.winner === 'tie';
  return (
    <div className="ai-cmp-result">
      {/* Export notice */}
      <div className="export-notice">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        This analysis is not saved after leaving. Export it to keep a record.
        <button className="export-pdf-btn-sm" onClick={onExportPDF}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Export PDF
        </button>
      </div>

      {/* Verdict banner */}
      <div className={`ai-verdict-banner ${isA ? 'verdict-a' : isB ? 'verdict-b' : 'verdict-tie'}`}>
        <div className="ai-verdict-header">
          {isTie ? 'It\'s a Tie' : <><Trophy size={15} style={{marginRight:6,verticalAlign:'middle'}} />{comparison.winnerName} is the stronger opportunity</>}
        </div>
        <div className="ai-verdict-text">{comparison.verdict}</div>
        <div className="ai-recommendation">{comparison.recommendation}</div>
      </div>

      {/* Score totals */}
      <div className="ai-score-totals">
        <div className={`ai-score-total ${isA ? 'score-total-winner' : ''}`}>
          <div className="ast-avatar" style={{ background: STATUS_COLORS[jobA.status]?.bg, color: STATUS_COLORS[jobA.status]?.text }}>
            {jobA.company[0]}
          </div>
          <div className="ast-info">
            <div className="ast-company">{jobA.company}</div>
            <div className="ast-score">{isA && '🏆 '}{comparison.totalScoreA.toFixed(1)} / 10</div>
          </div>
        </div>
        <div className="ai-score-divider">vs</div>
        <div className={`ai-score-total ${isB ? 'score-total-winner' : ''}`}>
          <div className="ast-info ast-info-right">
            <div className="ast-company">{jobB.company}</div>
            <div className="ast-score">{isB && '🏆 '}{comparison.totalScoreB.toFixed(1)} / 10</div>
          </div>
          <div className="ast-avatar" style={{ background: STATUS_COLORS[jobB.status]?.bg, color: STATUS_COLORS[jobB.status]?.text }}>
            {jobB.company[0]}
          </div>
        </div>
      </div>

      {/* Dimension breakdown */}
      <div className="ai-dimensions">
        <div className="ai-dim-header">
          <span>{jobA.company}</span>
          <span className="ai-dim-center-label">Dimension</span>
          <span>{jobB.company}</span>
        </div>
        {comparison.dimensions?.map((dim, i) => (
          <div key={i} className="ai-dim-row">
            <div className="ai-dim-scores">
              <DimBar scoreA={dim.scoreA} scoreB={dim.scoreB} winner={comparison.winner} />
            </div>
            <div className="ai-dim-label">
              <span className="ai-dim-icon">{dim.icon}</span>
              <span>{dim.name}</span>
            </div>
            <div className="ai-dim-reasoning">{dim.reasoning}</div>
          </div>
        ))}
      </div>

      {/* Strengths & weaknesses */}
      <div className="ai-swot">
        <div className="ai-swot-col">
          <div className="ai-swot-header ai-swot-good">✅ {jobA.company} Strengths</div>
          {comparison.jobAStrengths?.map((s, i) => <div key={i} className="ai-swot-item">{s}</div>)}
          <div className="ai-swot-header ai-swot-bad" style={{marginTop:10}}>⚠️ Concerns</div>
          {comparison.jobAWeaknesses?.map((w, i) => <div key={i} className="ai-swot-item">{w}</div>)}
        </div>
        <div className="ai-swot-col">
          <div className="ai-swot-header ai-swot-good">✅ {jobB.company} Strengths</div>
          {comparison.jobBStrengths?.map((s, i) => <div key={i} className="ai-swot-item">{s}</div>)}
          <div className="ai-swot-header ai-swot-bad" style={{marginTop:10}}>⚠️ Concerns</div>
          {comparison.jobBWeaknesses?.map((w, i) => <div key={i} className="ai-swot-item">{w}</div>)}
        </div>
      </div>
    </div>
  );
};

// ── Inner content ─────────────────────────────────────────────────────────────
const CompareContent = ({ jobs, onClose, inline, onUpgrade }) => {
  const { user, refreshUser } = useAuth();
  const isPro = user?.tier === 'pro';
  const [leftId,  setLeftId]  = useState(jobs[0]?._id || '');
  const [rightId, setRightId] = useState(jobs[1]?._id || '');
  const [aiResult, setAiResult] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiError, setAiError]   = useState('');

  const leftJob  = jobs.find(j => j._id === leftId);
  const rightJob = jobs.find(j => j._id === rightId);

  const runAICompare = async () => {
    if (!leftJob || !rightJob) return;
    setLoadingAI(true);
    setAiError('');
    setAiResult(null);
    try {
      const res = await api.post('/api/analyze/compare', { jobAId: leftId, jobBId: rightId });
      setAiResult(res.data);
      refreshUser();
    } catch (err) {
      setAiError(err.response?.data?.message || 'AI comparison failed. Check your GEMINI_API_KEY.');
    } finally {
      setLoadingAI(false);
    }
  };

  const exportComparisonPDF = () => {
    if (!aiResult) return;
    const { comparison, jobA, jobB } = aiResult;
    const winnerLine = comparison.winner === 'tie'
      ? "It's a Tie"
      : `Winner: ${comparison.winnerName} (${comparison.winner === 'A' ? jobA.company : jobB.company})`;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>ApplAI — AI Comparison</title>
<style>
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #F5F7FA; padding: 32px; color: #111827; }
  .header { background: linear-gradient(135deg,#2563EB,#14B8A6); color: white; border-radius: 12px; padding: 24px 28px; margin-bottom: 24px; }
  h1 { margin:0; font-size:22px; } .sub { margin:4px 0 0; opacity:0.85; font-size:13px; }
  .notice { background:#FFF3CD; border:1px solid #FDE68A; border-radius:8px; padding:10px 14px; font-size:12px; color:#92400E; margin-bottom:20px; }
  .verdict { background:#EFF6FF; border:1.5px solid #BFDBFE; border-radius:10px; padding:18px 20px; margin-bottom:20px; }
  .verdict-title { font-size:17px; font-weight:700; color:#1E3A8A; margin-bottom:8px; }
  .verdict-text { font-size:13.5px; color:#374151; margin-bottom:8px; line-height:1.6; }
  .rec { font-size:13px; color:#1D4ED8; font-weight:600; }
  .scores { display:flex; gap:20px; margin-bottom:20px; }
  .score-box { flex:1; background:white; border:1px solid #E2E6EE; border-radius:10px; padding:16px; text-align:center; }
  .score-company { font-size:14px; font-weight:700; margin-bottom:4px; }
  .score-num { font-size:28px; font-weight:800; color:#2563EB; }
  .winner-badge { background:#10B981; color:white; font-size:11px; padding:2px 8px; border-radius:20px; display:inline-block; margin-bottom:6px; }
  table { width:100%; border-collapse:collapse; margin-bottom:20px; }
  th { background:#F0F2F6; padding:10px 12px; text-align:left; font-size:12px; text-transform:uppercase; letter-spacing:0.05em; color:#6B7280; }
  td { padding:10px 12px; border-bottom:1px solid #E2E6EE; font-size:13px; }
  .swot { display:flex; gap:16px; }
  .swot-col { flex:1; background:white; border:1px solid #E2E6EE; border-radius:10px; padding:14px; }
  .swot-title { font-size:12px; font-weight:700; text-transform:uppercase; margin-bottom:8px; }
  .swot-item { font-size:12.5px; color:#374151; padding:4px 0; border-top:1px solid #F0F2F6; }
  .footer { margin-top:24px; text-align:center; font-size:11px; color:#9CA3AF; }
</style></head><body>
<div class="header"><h1>Application Comparison Analysis</h1><p class="sub">Generated by ApplAI · ${new Date().toLocaleDateString('en-PH', {weekday:'long',year:'numeric',month:'long',day:'numeric'})}</p></div>
<div class="notice">⚠️ This analysis was generated using AI and is not saved in ApplAI after leaving the page. Keep this PDF as your record.</div>
<div class="verdict"><div class="verdict-title">${winnerLine}</div><div class="verdict-text">${comparison.verdict}</div><div class="rec">→ ${comparison.recommendation}</div></div>
<div class="scores">
  <div class="score-box">${comparison.winner==='A'?'<div class="winner-badge">🏆 Winner</div>':''}<div class="score-company">${jobA.company} — ${jobA.role}</div><div class="score-num">${comparison.totalScoreA?.toFixed(1)}</div><div style="font-size:11px;color:#6B7280;">out of 10</div></div>
  <div class="score-box">${comparison.winner==='B'?'<div class="winner-badge">🏆 Winner</div>':''}<div class="score-company">${jobB.company} — ${jobB.role}</div><div class="score-num">${comparison.totalScoreB?.toFixed(1)}</div><div style="font-size:11px;color:#6B7280;">out of 10</div></div>
</div>
<table><thead><tr><th>Dimension</th><th>${jobA.company}</th><th>${jobB.company}</th><th>Notes</th></tr></thead><tbody>
${comparison.dimensions?.map(d=>`<tr><td>${d.icon} ${d.name}</td><td>${d.scoreA}/10</td><td>${d.scoreB}/10</td><td style="font-size:12px;color:#6B7280;">${d.reasoning}</td></tr>`).join('')}
</tbody></table>
<div class="swot">
  <div class="swot-col"><div class="swot-title" style="color:#15803D">✅ ${jobA.company} — Strengths</div>${comparison.jobAStrengths?.map(s=>`<div class="swot-item">${s}</div>`).join('')}<div class="swot-title" style="color:#B45309;margin-top:10px">⚠️ Concerns</div>${comparison.jobAWeaknesses?.map(w=>`<div class="swot-item">${w}</div>`).join('')}</div>
  <div class="swot-col"><div class="swot-title" style="color:#15803D">✅ ${jobB.company} — Strengths</div>${comparison.jobBStrengths?.map(s=>`<div class="swot-item">${s}</div>`).join('')}<div class="swot-title" style="color:#B45309;margin-top:10px">⚠️ Concerns</div>${comparison.jobBWeaknesses?.map(w=>`<div class="swot-item">${w}</div>`).join('')}</div>
</div>
<div class="footer">ApplAI · AI-powered job application tracker</div>
</body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  return (
    <>
      {/* Header */}
      <div className="cmp-header">
        <div className="cmp-header-left">
          <div className="cmp-header-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
          </div>
          <div>
            <h2 className="cmp-title">Compare Applications</h2>
            <p className="cmp-subtitle">Side-by-side view + optional AI analysis</p>
          </div>
        </div>
        {!inline && onClose && <button className="cmp-close" onClick={onClose}>✕</button>}
      </div>

      {/* Selectors */}
      <div className="cmp-selectors">
        <div className="cmp-selector-group">
          <label>Application A</label>
          <select className="cmp-select" value={leftId} onChange={e => { setLeftId(e.target.value); setAiResult(null); setAiError(''); }}>
            <option value="">— Select —</option>
            {jobs.filter(j => j._id !== rightId).map(j => <option key={j._id} value={j._id}>{j.company} — {j.role}</option>)}
          </select>
        </div>
        <div className="cmp-vs-badge">VS</div>
        <div className="cmp-selector-group">
          <label>Application B</label>
          <select className="cmp-select" value={rightId} onChange={e => { setRightId(e.target.value); setAiResult(null); setAiError(''); }}>
            <option value="">— Select —</option>
            {jobs.filter(j => j._id !== leftId).map(j => <option key={j._id} value={j._id}>{j.company} — {j.role}</option>)}
          </select>
        </div>
      </div>

      {leftJob && rightJob ? (
        <>
          {/* Side-by-side detail table + AI content — all in one scrollable body */}
          <div className="cmp-body">
            <div className="cmp-table-wrap">
            <table className="cmp-table">
              <thead>
                <tr>
                  <th className="cmp-th cmp-th-label">Field</th>
                  <th className="cmp-th cmp-th-val">
                    <div className="cmp-th-job">
                      <span className="cmp-th-avatar" style={{ background: STATUS_COLORS[leftJob.status]?.bg, color: STATUS_COLORS[leftJob.status]?.text }}>{leftJob.company[0]}</span>
                      {leftJob.company}
                    </div>
                  </th>
                  <th className="cmp-th cmp-th-val">
                    <div className="cmp-th-job">
                      <span className="cmp-th-avatar" style={{ background: STATUS_COLORS[rightJob.status]?.bg, color: STATUS_COLORS[rightJob.status]?.text }}>{rightJob.company[0]}</span>
                      {rightJob.company}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row, i) => {
                  const lColors = STATUS_COLORS[leftJob.status];
                  const rColors = STATUS_COLORS[rightJob.status];
                  return (
                    <tr key={row.key} className={i % 2 === 0 ? 'cmp-row-even' : ''}>
                      <td className="cmp-td cmp-td-label">{row.label}</td>
                      <td className="cmp-td cmp-td-val">
                        {row.isStatus ? (
                          <span className="cmp-status-pill" style={{ background: lColors?.bg, color: lColors?.text, border: `1px solid ${lColors?.border}` }}>{row.render(leftJob)}</span>
                        ) : <span className="cmp-val">{row.render(leftJob)}</span>}
                      </td>
                      <td className="cmp-td cmp-td-val">
                        {row.isStatus ? (
                          <span className="cmp-status-pill" style={{ background: rColors?.bg, color: rColors?.text, border: `1px solid ${rColors?.border}` }}>{row.render(rightJob)}</span>
                        ) : <span className="cmp-val">{row.render(rightJob)}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {/* AI Comparison CTA */}
            {!aiResult && (
              <div className={`cmp-ai-cta ${!isPro ? 'cmp-ai-cta-locked' : ''}`}>
                <div className="cmp-ai-cta-left">
                  <div className="cmp-ai-icon">{isPro ? '✨' : <Lock size={16} />}</div>
                  <div>
                    <div className="cmp-ai-cta-title">
                      AI Comparison Analysis
                      {!isPro && <span className="cmp-ai-pro-badge">Pro</span>}
                    </div>
                    <div className="cmp-ai-cta-sub">
                      {isPro
                        ? 'Scored across 6 dimensions — compensation, growth, work-life balance, stability, and more. With a verdict and recommendation.'
                        : 'Upgrade to Pro to unlock AI-powered comparison analysis. Basic side-by-side view is available above.'}
                    </div>
                  </div>
                </div>
                {isPro ? (
                  <button className="cmp-ai-btn" onClick={runAICompare} disabled={loadingAI}>
                    {loadingAI ? <><span className="btn-spinner" /> Analyzing...</> : '✨ Compare with AI →'}
                  </button>
                ) : (
                  <button className="cmp-ai-btn cmp-ai-btn-upgrade" onClick={onUpgrade}>
                    Upgrade to Pro →
                  </button>
                )}
              </div>
            )}

            {aiError && <div className="cmp-ai-error">⚠️ {aiError}</div>}

            {/* AI Result */}
            {aiResult && (
              <AIComparisonResult
                result={aiResult}
                jobA={leftJob}
                jobB={rightJob}
                onExportPDF={exportComparisonPDF}
              />
            )}
          </div>
        </>
      ) : (
        <div className="cmp-placeholder">
          <div className="cmp-placeholder-icon">⚖️</div>
          <p>Select two applications above to compare them side by side.</p>
        </div>
      )}
    </>
  );
};

const CompareModal = ({ jobs, onClose, inline = false, onUpgrade }) => {
  useEffect(() => {
    if (!inline) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [inline]);

  if (inline) {
    return <div className="cmp-panel cmp-panel-inline"><CompareContent jobs={jobs} onClose={onClose} inline={true} onUpgrade={onUpgrade} /></div>;
  }
  return (
    <div className="cmp-overlay" onClick={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="cmp-panel"><CompareContent jobs={jobs} onClose={onClose} inline={false} onUpgrade={onUpgrade} /></div>
    </div>
  );
};

export default CompareModal;
